import { MeasureValue } from './productSorting';

/*
 * Reading a number off a product's attributes: the one reader the Sort and Filter panel ranks
 * and filters with and the product cards show figures with, so a card never shows a figure the
 * panel disagrees with.
 */

/**
 * Structural rather than the full attribute union, so the table and the cards
 * can each pass their own product shape.
 */
export type RankableAttribute = {
  name?: { name?: string | null } | null;
  // `unknown`, not a union: the attribute union holds strings, booleans and
  // key-value lists. `parseMeasureNumber` narrows instead.
  value?: unknown;
  // `AttrNumberRange` has no `value` at all, so a market authoring a rate as a
  // range would rank every one of those products as missing.
  value_start?: unknown;
};

/**
 * Values arrive as display strings ("1,234.56", "12%"). Unparseable is missing,
 * never 0 — 0 would rank the product as the cheapest on the page.
 */
export const parseMeasureNumber = (raw: unknown): MeasureValue => {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  if (typeof raw !== 'string') return null;

  const cleaned = raw.replace(/[^0-9.-]/g, '');
  if (!cleaned || cleaned === '-' || cleaned === '.') return null;

  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
};

export const findAttributeValue = (
  attributes: readonly RankableAttribute[] | null | undefined,
  attributeName: string | undefined,
): MeasureValue => {
  if (!attributeName || !attributes?.length) return null;
  const match = attributes.find((a) => a?.name?.name === attributeName);
  // The low end of a range is one consistent number for every option rather
  // than the better end of each: a cover sorted highest-first still compares
  // its floor, so two ranges are ranked on the same basis.
  return parseMeasureNumber(match?.value ?? match?.value_start);
};

/**
 * Sorting rank for a card that states no income requirement.
 *
 * Roughly a fifth of the HK catalogue leaves `MINIMUM_ANNUAL_INCOME` empty, and
 * those cards are the most accessible ones — a reader sorting by income wants
 * them at the top, not dropped to the end with the products whose value could
 * not be read (CMS-2002 FR-3.2.7). A sentinel below every real requirement puts
 * them there without claiming the requirement is zero (FR-3.2.8): incomes are
 * non-negative, so nothing authored can collide with it.
 */
const NO_INCOME_REQUIREMENT_RANK = -1;

/**
 * The stated minimum income, with "no requirement" ranked first.
 *
 * An absent attribute and a blank value are both "no requirement"; a value that
 * is present but unreadable stays missing, so a broken figure sorts last rather
 * than being promoted to the top (FR-3.2.9).
 */
export const resolveMinimumIncomeValue = (
  attributes: readonly RankableAttribute[] | null | undefined,
  attributeName: string,
): MeasureValue => {
  const match = attributes?.find((a) => a?.name?.name === attributeName);
  const raw = match?.value ?? match?.value_start;
  if (raw == null || (typeof raw === 'string' && raw.trim() === '')) {
    return NO_INCOME_REQUIREMENT_RANK;
  }

  return parseMeasureNumber(raw);
};
